import { useEffect, useMemo, useState } from "react";
import EditableForm from "./EditableForm";
import { fetchAPI } from "../../../utils/utils";
import { withWebDefaultMethod } from "../utils/autoPaymentMethod";
import { newGuestId, rememberGuestId, storedGuestId } from "./guestId";

// How long the form waits for the person's saved answer before opening as a new one
const SAVED_ANSWER_TIMEOUT_MS = 8000;

function FillForm({ userId, isLogged, castellersInfo }) {
    const [formValues, setFormValues] = useState({});
    const [form, setForm] = useState(null);
    const [mote, setMote] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [loadedFormValuesScope, setLoadedFormValuesScope] = useState(null);
    // The person already answered this form: their answer keeps its own way to pay
    const [savedAnswer, setSavedAnswer] = useState(false);

    const evId = window.location.pathname.split('/')[2];
    // A guest coming back gets the answer they sent from this browser (guestId.js);
    // another answer ("Fes-ne una altra", or a member answering for someone else)
    // is a new guest, so it never replaces the one before
    const [guest, setGuest] = useState(() => {
        const stored = storedGuestId(evId);
        return { id: stored || newGuestId(), answered: Boolean(stored) };
    });
    const [forSomeoneElse, setForSomeoneElse] = useState(false);
    // The person had already answered: no need to ask again whether they have the app
    const [warningAccepted, setWarningAccepted] = useState(false);
    // Back from Stripe's page: ?pagament=<Checkout session>
    const [paymentSession] = useState(() => new URLSearchParams(window.location.search).get('pagament'));
    // Back from Stripe's page without paying: ?cancelat=1, what is left to pay comes first
    const [paymentCancelled] = useState(() => new URLSearchParams(window.location.search).get('cancelat') === '1');
    const isLoggedUser = Number.isInteger(userId) && userId !== -1;
    const asGuest = !isLoggedUser || forSomeoneElse;
    const effectiveUserId = asGuest ? guest.id : String(userId);
    const lookUpSavedAnswer = !asGuest || guest.answered;
    const formValuesScope = `${evId}:${effectiveUserId}`;

    useEffect(() => {
        if (paymentSession || paymentCancelled) window.history.replaceState(null, '', window.location.pathname);
    }, [paymentSession, paymentCancelled]);

    const startNewAnswer = () => {
        setForSomeoneElse(isLoggedUser);
        setGuest({ id: newGuestId(), answered: false });
        setWarningAccepted(true);
    };
    const onSubmitted = () => {
        if (asGuest) rememberGuestId(evId, guest.id);
    };

    useEffect(() => {
        let isMounted = true;
        const timeoutId = setTimeout(() => {
            if (!isMounted) return;
            setLoadError(true);
        }, 10000);

        setForm(null);
        setLoadError(false);

        fetchAPI(`/load_form/${evId}`, (data) => {
            if (!isMounted) return;
            clearTimeout(timeoutId);
            setForm(data);
            setLoadError(false);
        }, false);

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, [evId]);

    // The person's answer, again for each new one ("Fes-ne una altra"), without loading the form again
    useEffect(() => {
        let isMounted = true;
        setLoadedFormValuesScope(null);
        setSavedAnswer(false);

        let savedAnswerTimeoutId;
        if (lookUpSavedAnswer) {
            let opened = false;
            const openAsNewAnswer = () => {
                opened = true;
                setFormValues({});
                setLoadedFormValuesScope(formValuesScope);
            };
            // A saved answer that doesn't come doesn't keep the form from opening
            savedAnswerTimeoutId = setTimeout(() => {
                if (isMounted) openAsNewAnswer();
            }, SAVED_ANSWER_TIMEOUT_MS);

            fetchAPI(`/is_form_submitted/${encodeURIComponent(effectiveUserId)}/${evId}`, (res) => {
                if (!isMounted) return;
                clearTimeout(savedAnswerTimeoutId);
                // Late, it still fills the form: sending it empty would replace the answer
                const { mote: savedMote, ...saved } = res?.success && String(res?.evId) === String(evId) ? res.response || {} : {};
                // A guest's answer keeps the name they typed (VISITANT when they typed none)
                if (asGuest) setMote(savedMote && savedMote !== 'VISITANT' ? savedMote : '');
                setFormValues(saved);
                setSavedAnswer(Object.keys(saved).length > 0);
                if (Object.keys(saved).length > 0) setWarningAccepted(true);
                setLoadedFormValuesScope(formValuesScope);
            }, false, () => {
                // Without the saved answer the form still opens, as a new one
                if (!isMounted || opened) return;
                clearTimeout(savedAnswerTimeoutId);
                openAsNewAnswer();
            });
        } else {
            setFormValues({});
            setLoadedFormValuesScope(formValuesScope);
        }

        return () => {
            isMounted = false;
            clearTimeout(savedAnswerTimeoutId);
        };
    }, [asGuest, effectiveUserId, evId, formValuesScope, lookUpSavedAnswer]);

    // Shown with its first choices already in place (the way to pay), so nothing
    // changes once it is on screen. Cleared ("Fes-ne una altra"), it is a new answer.
    const formValuesLoaded = loadedFormValuesScope === formValuesScope;
    const shownFormValues = useMemo(
        () => (form && formValuesLoaded
            ? withWebDefaultMethod(form, formValues, { savedAnswer: savedAnswer && Object.keys(formValues || {}).length > 0 })
            : formValues),
        [form, formValues, formValuesLoaded, savedAnswer]
    );

    useEffect(() => {
        if (isLogged && !asGuest) {
            setMote(castellersInfo?.[userId]?.mote || '');
        }
    }, [asGuest, isLogged, userId, castellersInfo]);

    return (
        <div
            className="container"
            style={{
                width: '100%',
            }}
        >
            {
                loadError ? (
                    <div>
                        No s&apos;ha pogut carregar el formulari. Torna-ho a provar d&apos;aquí una estona.
                    </div>
                ) : form === null || !formValuesLoaded ? (
                    <div>
                        Carregant...
                    </div>
                ) : !form?.new ? (
                    <>
                        {/* <WelcomeUser
                            isLogged={isLogged}
                            userId={userId}
                            castellersInfo={castellersInfo}
                        /> */}
                        <EditableForm
                            key={effectiveUserId}
                            formSubmitted={false}
                            savedAnswer={savedAnswer}
                            paymentSession={paymentSession}
                            paymentCancelled={paymentCancelled}
                            warningAccepted={warningAccepted}
                            setWarningAccepted={setWarningAccepted}
                            onNewAnswer={startNewAnswer}
                            onSubmitted={onSubmitted}
                            formData={form}
                            formValues={shownFormValues}
                            formValuesLoaded={formValuesLoaded}
                            setFormValues={setFormValues}
                            evId={evId}
                            user={effectiveUserId}
                            mote={mote}
                            setMote={setMote}
                            castellersInfo={castellersInfo}
                            isLogged={isLogged && !asGuest}
                        />
                    </>
                ) : (
                    <div>
                        No hi ha formulari per aquest esdeveniment.
                    </div>
                )
            }
        </div>
    );
}

export default FillForm;
